import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal, Stack, Select, Checkbox, Group, Text, Button, ScrollArea, Center, Loader,
  Alert, Box, TextInput, Badge, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Search, Copy } from 'lucide-react';

import { membersApi, type Epoch, type Member } from '../api';
import { GroupTag } from './GroupTag';
import { numStyle } from '../theme';
import { plural } from '../utils/format';

interface Props {
  opened: boolean;
  epochs: Epoch[];
  activeEpochId: number | null;
  /** Jména už v aktivní epoše — ať se nekopírují duplikáty. */
  existingNames: string[];
  onClose: () => void;
}

/**
 * Kopírování členů z jiné epochy. Kopie se zakládá **bez skupiny a bez čísla** —
 * obojí je vázané na epochu a organizátor to přiřazuje až na místě. Členy bez čísla
 * mobilní aplikace nezobrazuje, takže do terénu se dostanou teprve po doplnění.
 */
export function CopyMembersModal({ opened, epochs, activeEpochId, existingNames, onClose }: Props) {
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [q, setQ] = useState('');
  const [copying, setCopying] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);

  const sources = epochs.filter((e) => e.id !== activeEpochId);

  useEffect(() => {
    if (!opened) return;
    setSourceId(sources[0] ? String(sources[0].id) : null);
    setPicked(new Set());
    setQ('');
    setFailed([]);
    setSkipped([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const source = useQuery({
    queryKey: ['members', 'epoch', sourceId],
    queryFn: () => membersApi.getAll(Number(sourceId)),
    enabled: opened && !!sourceId,
  });

  const taken = useMemo(
    () => new Set(existingNames.map((n) => n.trim().toLowerCase())),
    [existingNames]
  );

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (source.data ?? []).filter((m) => !needle || m.name.toLowerCase().includes(needle));
  }, [source.data, q]);

  const isTaken = (m: Member) => taken.has(m.name.trim().toLowerCase());

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copy = async () => {
    const chosen = (source.data ?? []).filter((m) => picked.has(m.id));
    if (chosen.length === 0) return;

    // Člen se stejným jménem v aktuální epoše už být může — to není chyba,
    // jen se nový nezakládá. Kontroluje se to tady, ne jen zablokovaným
    // zaškrtávátkem, protože seznam mohl mezitím zestárnout.
    const toCreate = chosen.filter((m) => !isTaken(m));
    const alreadyThere = chosen.filter((m) => isTaken(m)).map((m) => m.name);

    setCopying(true);
    setFailed([]);
    setSkipped(alreadyThere);
    const errors: string[] = [];

    // Postupně, ne paralelně — hromadný endpoint neexistuje a nechceme na server
    // pustit desítky requestů naráz.
    for (const m of toCreate) {
      try {
        await membersApi.create({ name: m.name, number: null, group_id: null });
      } catch (e: any) {
        errors.push(`${m.name}: ${e?.response?.data?.error || e.message}`);
      }
    }

    setCopying(false);
    qc.invalidateQueries({ queryKey: ['members'] });

    const ok = toCreate.length - errors.length;
    if (ok > 0) {
      notifications.show({
        message: `Zkopírováno ${plural(ok, 'člen', 'členové', 'členů')} — bez skupiny a bez čísla`,
        color: 'forest',
      });
    }
    if (alreadyThere.length > 0) {
      notifications.show({
        title: `Přeskočeno: ${alreadyThere.length}`,
        message: `${alreadyThere.join(', ')} — v aktuální epoše už ${
          alreadyThere.length === 1 ? 'je, nový se nevytvářel' : 'jsou, noví se nevytvářeli'
        }.`,
        color: 'blue',
      });
    }
    if (errors.length > 0) {
      setFailed(errors);
    } else {
      onClose();
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Zkopírovat členy z jiné epochy" size="lg">
      <Stack gap="sm">
        {sources.length === 0 ? (
          <Alert color="orange" title="Není odkud kopírovat">
            Kromě aktuální epochy tu žádná další není.
          </Alert>
        ) : (
          <>
            <Select
              label="Zdrojová epocha"
              data={sources.map((e) => ({ value: String(e.id), label: e.name }))}
              value={sourceId}
              onChange={(v) => { setSourceId(v); setPicked(new Set()); }}
              allowDeselect={false}
            />

            <Text c="dimmed" fz="sm">
              Kopie se založí <b>bez skupiny a bez čísla</b> — obojí patří k epoše a přiřadíš
              ho až tady. Dokud člen nemá číslo, mobilní aplikace ho nezobrazí.
            </Text>

            {source.isLoading && <Center h={120}><Loader size="sm" /></Center>}
            {source.error && (
              <Alert color="red" title="Členy se nepodařilo načíst">
                {(source.error as any)?.response?.data?.error || (source.error as any)?.message}
              </Alert>
            )}

            {source.isSuccess && (
              <>
                <TextInput
                  placeholder="Hledat jméno"
                  leftSection={<Search size={14} />}
                  value={q}
                  onChange={(e) => setQ(e.currentTarget.value)}
                />

                <Group justify="space-between">
                  <Group gap={6}>
                    <Button
                      size="xs" variant="default"
                      disabled={list.length === 0}
                      onClick={() => setPicked(new Set(list.map((m) => m.id)))}
                    >
                      Vybrat vše ({list.length})
                    </Button>
                    <Button size="xs" variant="subtle" color="gray"
                      disabled={picked.size === 0} onClick={() => setPicked(new Set())}>
                      Zrušit výběr
                    </Button>
                  </Group>
                  <Text fz="sm" c="dimmed">{plural(picked.size, 'vybraný', 'vybraní', 'vybraných')}</Text>
                </Group>

                {list.length === 0 ? (
                  <Text c="dimmed" ta="center" py="md" fz="sm">
                    {source.data?.length === 0
                      ? 'Tahle epocha nemá žádné členy.'
                      : 'Nikoho jsme nenašli.'}
                  </Text>
                ) : (
                  <ScrollArea.Autosize mah={300} type="auto">
                    <Stack gap={0}>
                      {list.map((m: Member) => {
                        const already = isTaken(m);
                        return (
                          <Group
                            key={m.id}
                            px="xs" py={7} gap="sm" wrap="nowrap"
                            style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
                          >
                            <Checkbox
                              checked={picked.has(m.id)}
                              onChange={() => toggle(m.id)}
                              aria-label={`Zkopírovat ${m.name}`}
                            />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fz="sm" fw={600} truncate>{m.name}</Text>
                              <Group gap={8}>
                                {m.number !== null && (
                                  <Text c="dimmed" fz={10} style={numStyle}>č. {m.number}</Text>
                                )}
                                <GroupTag name={m.group_name} color={m.group_color} />
                              </Group>
                            </Box>
                            {already && (
                              <Tooltip label="V aktuální epoše už člen s tímhle jménem je — přeskočí se.">
                                <Badge color="gray" size="sm">už tu je</Badge>
                              </Tooltip>
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  </ScrollArea.Autosize>
                )}
              </>
            )}

            {skipped.length > 0 && failed.length > 0 && (
              <Alert color="blue" title={`Přeskočeno: ${skipped.length}`}>
                {skipped.join(', ')} — v aktuální epoše už existují, noví se nevytvářeli.
              </Alert>
            )}

            {failed.length > 0 && (
              <Alert color="red" title={`Nezkopírováno: ${failed.length}`}>
                <Stack gap={2}>
                  {failed.map((f) => <Text key={f} fz="sm">{f}</Text>)}
                </Stack>
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Zavřít</Button>
          <Button
            leftSection={<Copy size={14} />}
            loading={copying}
            disabled={picked.size === 0}
            onClick={copy}
          >
            Zkopírovat ({picked.size})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
